import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { Upload, Trash2, Plus } from 'lucide-react'
import { toast } from 'react-toastify'

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/newRedesign'
import { Button } from '@/components/newRedesign'
import { Form } from '@/components/ui/form'
import { FormTextarea } from '@/components/newRedesign/form'
import { postMedia } from '@/api/mediaAPI'
import { cn } from '@/lib/utils'
import { DataTable } from '@/components/newRedesign/components/DataTable'
import { Input } from '@/components/newRedesign/primitives/Input'
import { ActionIconButton } from '@/components/newRedesign/components/ActionIconButton'

export interface DocumentFormData {
  observations?: string
}

interface PendingDocument {
  id: string
  name: string
  file: File
  size: string
}

interface DocumentDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  onSave: (_data: { name: string; path: string; observations?: string }) => void
  isPending?: boolean
}

export function DocumentDialog({
  open,
  onOpenChange,
  onSave,
  isPending = false,
}: DocumentDialogProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocument[]>(
    []
  )
  const [dragActive, setDragActive] = useState(false)
  const [editingDocId, setEditingDocId] = useState<string | null>(null)

  const form = useForm<DocumentFormData>({
    defaultValues: {
      observations: '',
    },
  })

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => {
      setIsOnline(false)
      if (isUploading) {
        setIsUploading(false)
        toast.error('Se perdió la conexión a internet.')
      }
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [isUploading])

  const uploadMutation = useMutation({
    mutationFn: postMedia,
  })

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset({
        observations: '',
      })
      setPendingDocuments([])
      setEditingDocId(null)
      setDragActive(false)
    }
  }, [open, form])

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  const handleFilesSelect = (files: FileList | null) => {
    if (!files) return

    const newPendingDocs: PendingDocument[] = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      name: file.name,
      file: file,
      size: formatFileSize(file.size),
    }))

    setPendingDocuments((prev) => [...prev, ...newPendingDocs])
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragActive(true)
  }

  const handleDragLeave = () => {
    setDragActive(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragActive(false)
    handleFilesSelect(e.dataTransfer.files)
  }

  const handleRemovePendingDocument = (id: string) => {
    setPendingDocuments((prev) => prev.filter((doc) => doc.id !== id))
  }

  const handleEditPendingDocumentName = (id: string, newName: string) => {
    setPendingDocuments((prev) =>
      prev.map((doc) => (doc.id === id ? { ...doc, name: newName } : doc))
    )
  }

  const handleSubmit = async (data: DocumentFormData) => {
    if (pendingDocuments.length === 0) {
      toast.error('Debe seleccionar al menos un archivo')
      return
    }

    if (!navigator.onLine || !isOnline) {
      toast.error(
        'No hay conexión a internet. Por favor, verifica tu conexión.'
      )
      return
    }

    setIsUploading(true)

    try {
      let successCount = 0
      for (const doc of pendingDocuments) {
        const fileExtension =
          doc.file.name.split('.').pop()?.toLowerCase() || 'png'

        const mediaParams = {
          params: {
            documentType: 'IMAGE',
            size: Math.ceil(doc.file.size / (1024 * 1024)) || 1,
            ext: fileExtension,
            isPrivate: true,
            name: doc.name,
          },
          file: doc.file,
        }

        const path = await uploadMutation.mutateAsync(mediaParams)

        onSave({
          name: doc.name,
          path: path,
          observations: data.observations,
        })
        successCount++
      }

      setIsUploading(false)

      if (successCount === 1) {
        toast.success('Documento adjuntado correctamente')
      } else {
        toast.success(`${successCount} documentos adjuntados correctamente`)
      }

      // Reset and close
      setPendingDocuments([])
      form.reset()
      onOpenChange(false)
    } catch (error) {
      setIsUploading(false)
      if (!navigator.onLine) {
        toast.error(
          'No hay conexión a internet. Por favor, verifica tu conexión.'
        )
      } else if ((error as Error).message?.includes('NetworkError')) {
        toast.error('Error de red. No se pudo subir el archivo.')
      } else if ((error as Error).message?.includes('timeout')) {
        toast.error('Tiempo de espera agotado. Por favor, intenta nuevamente.')
      } else {
        toast.error(
          `Error al subir el archivo: ${
            (error as Error).message || 'Error desconocido'
          }`
        )
      }
    }
  }

  const isButtonDisabled =
    isPending || isUploading || !isOnline || pendingDocuments.length === 0

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className={cn(
          'flex h-auto w-full flex-col overflow-hidden bg-[#FAFAFA] p-0 shadow-lg',
          'max-h-[90vh] max-w-[95vw]',
          'sm:max-h-[85vh] sm:max-w-[90vw]',
          'md:max-w-[800px]',
          'lg:max-w-[900px]',
          'xl:max-w-[1000px]'
        )}
      >
        <DialogHeader
          className={cn(
            'flex shrink-0 flex-row items-center justify-between',
            'border-b border-neutral-300',
            'px-4 py-2',
            'sm:px-5 sm:py-2',
            'lg:px-6 lg:py-2.5'
          )}
        >
          <DialogTitle
            className={cn(
              'text-left font-semibold leading-tight tracking-tight',
              'text-base text-neutral-800',
              'sm:text-lg',
              'lg:text-xl'
            )}
          >
            Adjuntar documentos
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <div
            className={cn(
              'flex flex-1 flex-col overflow-y-auto bg-[#FAFAFA]',
              'gap-4 px-4 py-4'
            )}
          >
            {pendingDocuments.length === 0 ? (
              <div className="flex flex-col gap-4">
                <div
                  className={cn(
                    'flex h-[400px] cursor-pointer flex-col',
                    'items-center justify-center gap-2 rounded-md',
                    'border-2 border-dashed p-6 transition-colors',
                    dragActive
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-[#D4D4D4] bg-transparent'
                  )}
                  onClick={() =>
                    document.getElementById('file-input-multiple')?.click()
                  }
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <Upload className="h-12 w-12 text-gray-500" />
                  <span className="text-center text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                    Arrastra y suelta archivos o haz clic para seleccionar
                  </span>
                  <span className="text-center text-xs leading-4 tracking-[0.015em] text-[#737373]">
                    Permitidos: PDF, imágenes, Word, Excel
                  </span>
                  <input
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    className="hidden"
                    id="file-input-multiple"
                    multiple
                    onChange={(e) => handleFilesSelect(e.target.files)}
                    type="file"
                  />
                </div>
              </div>
            ) : (
              <div className="flex max-h-[400px] min-h-[350px] flex-col gap-4 lg:min-h-[400px]">
                <div className="flex shrink-0 flex-row flex-wrap items-center gap-2">
                  <Button
                    className="h-9 min-w-9 gap-2 rounded-md bg-[#1E293B] px-4 hover:bg-[#1E293B]/90"
                    onClick={() =>
                      document.getElementById('file-input-multiple')?.click()
                    }
                    size="sm"
                    type="button"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="text-sm font-medium leading-5 tracking-[0.005em]">
                      Archivo
                    </span>
                  </Button>
                </div>
                <input
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  className="hidden"
                  id="file-input-multiple"
                  multiple
                  onChange={(e) => handleFilesSelect(e.target.files)}
                  type="file"
                />
                <div
                  className={cn(
                    'flex min-h-0 flex-1 flex-col overflow-hidden',
                    'rounded-md border border-[#D4D4D4] bg-[#FAFAFA]'
                  )}
                >
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <DataTable
                      alignColumns={true}
                      columns={['Nombre', 'Tamaño', 'Acciones']}
                      data={pendingDocuments}
                      emptyMessage=""
                      renderRow={(doc) => {
                        const lastDotIndex = doc.name.lastIndexOf('.')
                        const hasExtension = lastDotIndex !== -1
                        const nameWithoutExt = hasExtension
                          ? doc.name.substring(0, lastDotIndex)
                          : doc.name
                        const extension = hasExtension
                          ? doc.name.substring(lastDotIndex)
                          : ''
                        const isEditing = editingDocId === doc.id

                        return (
                          <>
                            <td className="border-b border-neutral-300 px-2 py-2">
                              <Input
                                className={cn(
                                  'shadow-xs h-8 w-full text-sm',
                                  'border-[#D4D4D4] bg-[#FAFAFA]'
                                )}
                                onBlur={() => setEditingDocId(null)}
                                onChange={(e) =>
                                  handleEditPendingDocumentName(
                                    doc.id,
                                    isEditing
                                      ? e.target.value + extension
                                      : e.target.value
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                                onFocus={() => setEditingDocId(doc.id)}
                                type="text"
                                value={isEditing ? nameWithoutExt : doc.name}
                              />
                            </td>
                            <td
                              className={cn(
                                'w-24 border-b border-neutral-300 px-3 py-2',
                                'text-left text-sm leading-5',
                                'tracking-[0.005em] text-[#262626]'
                              )}
                            >
                              {doc.size}
                            </td>
                            <td className="border-b border-neutral-300 px-2 py-2 text-right">
                              <div className="flex items-center justify-end">
                                <ActionIconButton
                                  className={cn(
                                    'h-8 w-8 rounded-md',
                                    'text-[#DC2626] hover:bg-red-50'
                                  )}
                                  icon={<Trash2 className="h-4 w-4" />}
                                  label="Eliminar"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleRemovePendingDocument(doc.id)
                                  }}
                                  size="icon"
                                  variant="ghost"
                                />
                              </div>
                            </td>
                          </>
                        )
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Observaciones */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Observaciones
              </label>
              <FormTextarea
                control={form.control}
                disabled={false}
                name="observations"
                placeholder="Observaciones (aplicarán a todos los documentos)"
                rows={3}
                showLabel={false}
              />
            </div>
          </div>

          <DialogFooter
            className={cn(
              'flex shrink-0 items-center justify-end',
              'border-t border-neutral-300',
              'px-4 py-2',
              'sm:px-5 sm:py-2',
              'lg:px-6 lg:py-2.5'
            )}
          >
            <Button
              className={cn(
                'h-9 w-full gap-2',
                'bg-slate-800 hover:bg-slate-700',
                'sm:w-auto sm:min-w-[143px]'
              )}
              disabled={isButtonDisabled}
              icon={<Upload className="h-4 w-4" />}
              onClick={form.handleSubmit(handleSubmit)}
              title={
                !isOnline
                  ? 'Sin conexión a internet'
                  : pendingDocuments.length === 0
                    ? 'Debe seleccionar al menos un archivo'
                    : undefined
              }
              type="button"
              variant="default"
            >
              {!isOnline
                ? 'Sin conexión'
                : isUploading
                  ? 'Subiendo archivos...'
                  : isPending
                    ? 'Guardando...'
                    : pendingDocuments.length > 1
                      ? 'Subir archivos'
                      : 'Subir archivo'}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
